import { useState } from 'react';

import styled from 'styled-components';

import { Card } from '@components/card';
import { getRandomAvatarUrl } from '@components/design-avatar';
import { AVATAR_GROUPS } from '@components/design-avatar/avatars';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconHourglass } from '@components/icons/hourglass';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { JsonValue } from '@components/json-editor';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { NoResultCard } from '@components/no-result-card';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useHiddenModels } from 'api/ai-agents/hooks';
import { createJourneyActionFieldPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import {
  AIModel,
  AIReasoningEffort,
  TravellerJourneyActionDetailed,
  VariableValue,
} from 'api/types';
import {
  DEFAULT_MODEL,
  getReasoningEffortConfig,
  normalizeReasoningEffortForModel,
} from 'ui/authenticated/main/pages/ai-agents/ai-agent-config';
import { useModelSelectorModal } from 'ui/authenticated/main/pages/ai-agents/modal-selector-modal';
import { AIModelSelector } from 'ui/authenticated/main/pages/ai-agents/model-selector';
import { useOutputModal } from 'ui/authenticated/main/pages/ai-agents/output-modal';
import { Property } from 'ui/authenticated/main/pages/ai-agents/shared/output-list';
import {
  AIAgentConfigurationFields,
  AIAgentKnowledgeFilesField,
  AIAgentOutputFields,
  AIAgentPromptField,
  AIAgentToolsFields,
  UploadedFile,
  fileUrlsToUploadedFiles,
} from 'ui/authenticated/main/pages/ai-agents/shared/sidebar-form-sections';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { ACTION_TYPES } from '../action-config';
import {
  ActionResultCard,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  useActionTabs,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.ask_ai_agent;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <DetailsContent action={action} /> : <></>,
    edit: action ? <FormContent action={action} /> : <></>,
  });

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader
        actionType={ACTION_TYPE}
        title={t('title')}
        customIcon={<IconAiAgent variant="gradient" />}
        tabs={tabs}
      >
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const FieldMappedTag = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 1,
})({
  backgroundColor: theme.color.blueTrans10,
  borderColor: theme.color.elementBlue100,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  color: theme.color.elementBlue100,
  borderRadius: pxToRem(16),
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
  height: pxToRem(24),
  display: 'inline-flex',
  maxWidth: pxToRem(400),
  overflow: 'hidden',
  flexShrink: 0,
});

const TextWithTagsContainer = styled(Text)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: pxToRem(4),
  width: '100%',
});

/*
We probably want to refactor this to use a more generic approach to rendering text with tags.
I'll keep this as is for now, but once we use this in other places, we should move it to core
and possibly rewrite it.
*/
const TextWithTags = ({ data }: { data: VariableValue | undefined }) => {
  // Function to replace variables with tags
  const renderTextWithTags = (value: string, variables: VariableValue['variables']) => {
    const parts = value.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{([^}]+)\}\}/);
      if (match) {
        const variableKey = match[1].trim();
        const variable = variables?.[variableKey];
        if (variable) {
          return (
            <FieldMappedTag key={index}>
              <IconTextFormatLink />
              <Text
                font="semibold12"
                color="elementBlue100"
                text={variable.label}
                ellipsis
                style={{ minWidth: 0, flex: 1 }}
              />
            </FieldMappedTag>
          );
        }
      }
      return part;
    });
  };
  if (!data) {
    return null;
  }

  return (
    <TextWithTagsContainer>
      {renderTextWithTags(data.value, data.variables ?? {})}
    </TextWithTagsContainer>
  );
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const isComplete = action.status === 'completed';
  const outputSettings = action.data.output ?? [];

  const outputCards = outputSettings.map((output: Property) => (
    <Card key={output.name} spaceX={3} spaceY={1} interactive>
      <Stack orientation="horizontal" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center">
          <AttributeIcon type={output.type} />
          <Text text={output.label} />
          <IconInfoSmall color="purple50" tooltip={output.instructions} />
        </Stack>
      </Stack>
    </Card>
  ));

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionResultCard
          action={action}
          title={t('title')}
          icon={<IconAiAgent variant="gradient" />}
          errorTitle={actionErrorTitle[action.action]}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <LabelTextGroup
            label={t('prompt')}
            text={isComplete ? action.data.prompt : <TextWithTags data={action.values?.prompt} />}
          />
          <div>
            <Text font="semibold14" text={t('output')} />
            <Stack space={1}>{outputCards}</Stack>
          </div>
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('output')}>
        <ActionOutput action={action} outputSettings={outputSettings} />
      </HideContainer>
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('run')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const ActionOutput = ({
  action,
  outputSettings,
}: ContentProps & { outputSettings: Array<Property> }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  if (action.status !== 'completed') {
    return <NoResultCard icon={IconHourglass} text={t('actionNotRun')} />;
  }
  const response = action.data.response as Record<string, string>;
  return (
    <Stack space={2}>
      {outputSettings.map((property) => (
        <div key={property.name}>
          <Text font="semibold14" text={property.label} />
          {property.type === 'boolean' ? (
            response[property.name] ? (
              <IconCheckmarkSmall color="green200" />
            ) : (
              <IconCloseSmall color="red200" />
            )
          ) : (
            <Text font="regular14" text={response[property.name] ?? response['__text'] ?? ''} />
          )}
        </div>
      ))}
    </Stack>
  );
};

type Data = {
  name?: string;
  picture?: string;
  id: string;
  description: string;
  prompt: VariableValue;
  additionalFiles: Array<VariableValue>;
  files: UploadedFile[];
  reasoningEffort?: AIReasoningEffort;
  systemInstructions: string;
  model: AIModel;
  jsonSchema?: JsonValue;
  output: Property[];
  useWebSearch: boolean;
  useFileSearch: boolean;
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateFormStore();
  const [initialPicture] = useState(() => action.values?.picture ?? getRandomAvatarUrl());
  const defaultOutput = action.values?.output ?? [];
  const currentOutput = formStore.useFieldValue('output') as Property[] | undefined;
  const output = currentOutput ?? defaultOutput;
  const model = formStore.useFieldValue('model') as AIModel;
  const { data: hiddenModelsData } = useHiddenModels();
  const hiddenModels = hiddenModelsData?.models ?? [];
  const resolvedModel = model ?? action.values?.model ?? DEFAULT_MODEL;
  const reasoningEffortConfig = resolvedModel ? getReasoningEffortConfig(resolvedModel) : undefined;
  const defaultKnowledgeFiles = fileUrlsToUploadedFiles(action.values?.files);
  const additionalFiles =
    (formStore.useFieldValue('additionalFiles') as Array<VariableValue> | undefined) ??
    action.values?.additionalFiles ??
    [];
  const isDirty = formStore.useIsDirty();
  const hasOutputError = formStore.useFormErrors().output;
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const showOutputModal = useOutputModal();
  const showModelSelectorModal = useModelSelectorModal();

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleOutputModal = (index?: number) => {
    showOutputModal({
      setValue: (value) => formStore.actions.setValue('output', value),
      getValue: () => formStore.getState().values['output'] as Property[],
      currentIndex: index,
    });
  };

  const handleDeleteOutput = (index: number) => {
    const state = formStore.getState();
    const currentOutput = (state.values['output'] ?? []) as Property[];
    formStore.actions.setValue(
      'output',
      currentOutput.filter((_, i) => i !== index),
    );
  };

  const handleSave = async (formData: Data) => {
    const resolvedAdditionalFiles = formData.additionalFiles ?? additionalFiles;
    const resolvedKnowledgeFiles = formData.files ?? defaultKnowledgeFiles;
    const newValues = {
      ...action.values,
      ...formData,
      picture: formData.picture ?? initialPicture,
      files: resolvedKnowledgeFiles.map((file) => file.url),
      additionalFiles: resolvedAdditionalFiles,
      output: formData.output?.length ? formData.output : output,
    };
    await editMutation.mutateAsync({
      description: formData.description,
      isAutomatic: true,
      action: 'ask_ai_agent',
      values: { ...newValues, jsonSchema: newValues.jsonSchema ?? undefined },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const fileGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['file', 'file_multi'],
  });

  const uploadMutation = useUploadFile(
    createJourneyActionFieldPath(action.journeyId, action.shortId),
    true,
  );
  const pictureUploadMutation = useUploadFile(
    createJourneyActionFieldPath(action.journeyId, action.shortId),
    false,
  );

  const handleSelectModel = (nextModel: AIModel) => {
    formStore.actions.setValue('model', nextModel);

    const currentReasoningEffort =
      (formStore.getState().values['reasoningEffort'] as AIReasoningEffort | undefined) ??
      action.values?.reasoningEffort ??
      null;
    const normalizedReasoningEffort = normalizeReasoningEffortForModel(
      nextModel,
      currentReasoningEffort,
    );

    if (normalizedReasoningEffort) {
      formStore.actions.setValue('reasoningEffort', normalizedReasoningEffort);
    }
  };

  const handleOpenAIModelsModal = () => {
    showModelSelectorModal({
      currentModel: resolvedModel,
      hiddenModels,
      onSelectModel: handleSelectModel,
    });
  };
  const currentSystemInstructions = formStore.useFieldValue('systemInstructions') as
    | string
    | undefined;
  const resolvedSystemInstructions =
    currentSystemInstructions ?? action.values?.systemInstructions ?? '';
  const isGenerateOutputDisabled = resolvedSystemInstructions.trim().length === 0;

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('agentDetails')}>
          <Stack space={4}>
            <Form.DesignAvatarChooserField
              id="picture"
              label={t('agentImage')}
              avatars={AVATAR_GROUPS.robots}
              onSelect={pictureUploadMutation.mutateAsync}
              defaultImage={initialPicture}
            />
            <Form.TextField id="name" label={t('agentName')} defaultValue={action.values?.name} />
            <DescriptionFormField defaultValue={action.description} />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('configuration')}>
          <Stack space={4}>
            <AIAgentConfigurationFields
              Form={Form}
              defaultModel={action.values?.model ?? DEFAULT_MODEL}
              resolvedModel={resolvedModel}
              defaultSystemInstructions={action.values?.systemInstructions}
              defaultReasoningEffort={action.values?.reasoningEffort}
              reasoningEffortConfig={reasoningEffortConfig}
              onOpenModelSelector={handleOpenAIModelsModal}
              renderModelSelector={({ model, onOpen }) => (
                <AIModelSelector model={model as AIModel} onOpen={onOpen} />
              )}
            />
            <AIAgentKnowledgeFilesField
              Form={Form}
              isTemplate={false}
              fieldId="files"
              defaultFiles={defaultKnowledgeFiles}
              onSelect={uploadMutation.mutateAsync}
              additionalFilesConfig={{
                fieldId: 'additionalFiles',
                variableGroups: fileGroups,
                defaultValue: action.values?.additionalFiles,
                fieldMappingId,
              }}
            />
            <HideContainer title={t('tools')}>
              <AIAgentToolsFields
                Form={Form}
                model={resolvedModel}
                defaultUseWebSearch={action.values?.useWebSearch}
                defaultUseFileSearch={action.values?.useFileSearch}
              />
            </HideContainer>
            <HideContainer title={t('input')}>
              <AIAgentPromptField
                Form={Form}
                isTemplate={false}
                variableGroups={variableGroups}
                defaultValue={action.values?.prompt}
                fieldMappingId={fieldMappingId}
              />
            </HideContainer>
            <HideContainer title={t('output')}>
              <AIAgentOutputFields
                Form={Form}
                output={output}
                defaultOutput={defaultOutput}
                systemInstructions={resolvedSystemInstructions}
                setOutput={(value) => formStore.actions.setValue('output', value)}
                handleOutputModal={handleOutputModal}
                handleDeleteOutput={handleDeleteOutput}
                hasOutputError={hasOutputError}
                isGenerateOutputDisabled={isGenerateOutputDisabled}
              />
            </HideContainer>
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    title: 'AI Agent',
    agentDetails: 'Agent details',
    actionDetails: 'Action details',
    agentImage: 'Agent image',
    agentName: 'Agent name',
    configuration: 'Configuration',
    knowledge: 'Knowledge',
    tools: 'Tools',
    systemInstructions: 'System instructions',
    input: 'Input',
    output: 'Output',
    prompt: 'Prompt',
    actionNotRun: 'Action has not been run',
    dataNotFound: 'Data not found',
    description: 'Description',
    run: 'Run',
    retry: 'Retry',
    actionStatus: 'Action status',
  },
};
